import { IElement } from '@shared/models/project.model';
import { IDataTable } from '@shared/models/data-table.model';
import { ExpressionVariables } from '../expression-variables.helpers';
import { updateInheritedPropertyFallbacks } from '../project.helpers';
import { applyElementCategoryProperties } from '../element-category.helpers';
import { syncDataTableProperties } from '../data-tables/data-table-pipeline.helpers';
import { autoUpdateElementQuantity } from '../auto-quantity.helpers';
import { getElementProperties } from '../element-property.helpers';
import { ElementChange, ProjectChangeType } from './update-engine.models';
import { ElementPath } from '../recursive-element.helpers';
import { projectGuards } from '../guards/project-guards.helpers';
import { isEqual } from 'lodash';

/**
 * IMPORTANT! This file currently works as a bridge between key parts of the previous pipeline and the new one.
 * It's important to keep in mind that we probably need to rethink this a bit
 */

/**
 * Alternative to {@link updateInheritedPropertyFallbacks} that returns
 * {@link ElementChange}[] describing the fallbackCount changes.
 *
 * Diffs original vs enriched element and emits one update per changed property.
 * @param path Ancestor path (same as {@link forEachElement}); immediate parent is used for DEV-3354 quantity-row checks.
 */
export const computeInheritedFallbackUpdates = (
  element: IElement,
  nodeVars: ExpressionVariables,
  path: ElementPath = [],
): ElementChange[] => {
  const parentFromPath = path.length > 0 ? path[path.length - 1] : undefined;
  const parentElement =
    parentFromPath && projectGuards.is.element(parentFromPath)
      ? parentFromPath
      : undefined;
  const enriched = updateInheritedPropertyFallbacks(
    element,
    nodeVars,
    parentElement,
  );
  if (enriched === element) return [];

  return diffPropertyFallbackCounts(element, enriched);
};

/**
 * Alternative to {@link applyElementCategoryProperties} that returns
 * {@link ElementChange}[] describing the structural changes.
 * TODO: This can be optimized now when we can use granular updates
 * Captures added/removed properties and children from category definition.
 */
export const computeCategoryPropertyUpdates = (
  element: IElement,
): ElementChange[] => {
  const enriched = applyElementCategoryProperties(element);
  if (enriched === element) return [];

  return diffCategoryEnrichment(element, enriched);
};

/**
 * Alternative to {@link syncDataTableProperties} that returns
 * {@link ElementChange}[] describing the property count changes.
 * TODO: This can be optimized now when we can use granular updates
 *
 *
 * Diffs original vs enriched element and emits one update per changed count.
 */
export const computeDataTableSyncUpdates = (
  element: IElement,
  dataTables: IDataTable[],
): ElementChange[] => {
  const enriched = syncDataTableProperties(element, dataTables);
  if (enriched === element) return [];

  return diffPropertyCounts(element, enriched);
};

/**
 * Alternative to {@link autoUpdateElementQuantity} that returns
 * {@link ElementChange}[] describing the quantity record changes.
 * TODO: This can be optimized now when we can use granular updates
 * Emits a single update with the full updated quantity record when changed.
 */
export const computeAutoQuantityUpdates = (
  element: IElement,
  path: ElementPath,
  nodeVars: ExpressionVariables,
): ElementChange[] => {
  const enriched = autoUpdateElementQuantity(
    element,
    path,
    {
      [element.id]: nodeVars,
    },
    { cacheNamespace: 'compute' },
  );
  if (enriched === element) return [];

  return [
    {
      type: ProjectChangeType.ElementQuantityAutoUpdate,
      elementId: element.id,
      quantity: enriched.quantity,
    },
  ];
};

// ── Diff helpers ─────────────────────────────────────────────

/**
 * Compare properties between original and enriched element,
 * emit {@link ProjectChangeType.ElementPropertyFallbackCount} for each changed fallbackCount.
 */
const diffPropertyFallbackCounts = (
  original: IElement,
  enriched: IElement,
): ElementChange[] => {
  const originalProps = getElementProperties(original);
  const enrichedProps = getElementProperties(enriched);
  const changes: ElementChange[] = [];

  for (const enrichedProp of enrichedProps) {
    const originalProp = originalProps.find(
      (p) => p.name === enrichedProp.name,
    );
    if (!originalProp) continue;

    // Only emit if fallbackCount actually changed
    if (!isEqual(originalProp.fallbackCount, enrichedProp.fallbackCount)) {
      changes.push({
        type: ProjectChangeType.ElementPropertyFallbackCount,
        elementId: original.id,
        propertyName: enrichedProp.name,
        fallbackCount: enrichedProp.fallbackCount,
      });
    }
  }

  return changes;
};

/**
 * Compare properties between original and enriched element,
 * emit {@link ProjectChangeType.ElementPropertyCount} for each changed count.
 */
const diffPropertyCounts = (
  original: IElement,
  enriched: IElement,
): ElementChange[] => {
  const originalProps = getElementProperties(original);
  const enrichedProps = getElementProperties(enriched);
  const changes: ElementChange[] = [];

  for (const enrichedProp of enrichedProps) {
    const originalProp = originalProps.find(
      (p) => p.name === enrichedProp.name,
    );
    if (!originalProp) continue;

    if (!isEqual(originalProp.count, enrichedProp.count)) {
      changes.push({
        type: ProjectChangeType.ElementPropertyCount,
        elementId: original.id,
        propertyName: enrichedProp.name,
        count: enrichedProp.count,
      });
    }
  }

  return changes;
};

/**
 * Diff original vs enriched element for category enrichment.
 * Produces a single {@link ProjectChangeType.ElementCategoryEnrich} change
 * that captures added/removed properties and children.
 *
 * @param ownEntryChildIds Ids of existing children that have their own enriched-map
 *   entry. Their structural changes are emitted by that entry, so the parent must not
 *   also remove + re-add them — doing so duplicates ids when both are applied (DEV-3421).
 *   Genuinely new children (not in `original`) are still added here since their own
 *   entry can't insert an element that does not yet exist in the project.
 */
const diffCategoryEnrichment = (
  original: IElement,
  enriched: IElement,
  ownEntryChildIds?: Set<string>,
): ElementChange[] => {
  const originalPropNames = new Set(
    getElementProperties(original).map((p) => p.name),
  );
  const enrichedPropNames = new Set(
    getElementProperties(enriched).map((p) => p.name),
  );

  // Properties added by category
  const addProperties = getElementProperties(enriched).filter(
    (p) => !originalPropNames.has(p.name),
  );

  // Property names removed by category
  const removePropertyNames = [...originalPropNames].filter(
    (name) => !enrichedPropNames.has(name),
  );

  // Children added by category, or replaced (same id but different content)
  const originalChildrenById = new Map(original.elements.map((c) => [c.id, c]));
  const addChildren = enriched.elements.filter((c) => {
    const orig = originalChildrenById.get(c.id);
    if (!orig) return true;
    // Existing child handled by its own enriched entry — skip to avoid duplicate ids
    if (ownEntryChildIds?.has(c.id)) return false;
    return !isEqual(orig, c);
  });

  // Children removed by category, or replaced (so we can add the enriched version)
  const enrichedChildrenById = new Map(enriched.elements.map((c) => [c.id, c]));
  const removeChildIds = original.elements
    .filter((c) => {
      const enr = enrichedChildrenById.get(c.id);
      if (!enr) return true;
      // Existing child handled by its own enriched entry — leave it in place
      if (ownEntryChildIds?.has(c.id)) return false;
      return !isEqual(c, enr);
    })
    .map((c) => c.id);

  // Skip if nothing actually changed
  if (
    !addProperties.length &&
    !removePropertyNames.length &&
    !addChildren.length &&
    !removeChildIds.length
  ) {
    return [];
  }

  return [
    {
      type: ProjectChangeType.ElementCategoryEnrich,
      elementId: original.id,
      addProperties,
      removePropertyNames,
      addChildren,
      removeChildIds,
    },
  ];
};

/**
 * Build structural changes (category + quantity) by diffing original vs enriched element.
 * Used when applying enriched elements via applyProjectChanges instead of updateElements.
 *
 * @param original - Current element in project
 * @param enriched - Structurally enriched element from compute
 * @param ownEntryChildIds - Ids of children that have their own enriched-map entry
 *   (see {@link diffCategoryEnrichment}); skipped from this element's child diff.
 * @returns ElementChange[] to pass to applyProjectChanges
 */
export const enrichedElementToStructuralChanges = (
  original: IElement,
  enriched: IElement,
  ownEntryChildIds?: Set<string>,
): ElementChange[] => {
  const categoryChanges = diffCategoryEnrichment(
    original,
    enriched,
    ownEntryChildIds,
  );
  const fallbackChanges = diffPropertyFallbackCounts(original, enriched);

  const enrichedQty = enriched.quantity;
  const quantityChanged =
    enrichedQty && !isEqual(original.quantity, enrichedQty);

  const quantityChanges: ElementChange[] = quantityChanged
    ? [
        {
          type: ProjectChangeType.ElementQuantityAutoUpdate,
          elementId: enriched.id,
          quantity: enrichedQty,
        },
      ]
    : [];

  return [...categoryChanges, ...fallbackChanges, ...quantityChanges];
};
